import { colors } from './colors';

export const homePageStyles = {
  // Container styles
  container: {
    py: 4,
    bgcolor: colors.background.default,
    minHeight: '100vh',
    fontFamily: 'Inter, system-ui, sans-serif'
  },

  // Loading and error states
  loadingContainer: {
    py: 4,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '50vh',
    bgcolor: '#F4F7FE'
  },
  loadingSpinner: {
    color: colors.primary.main
  },
  errorContainer: {
    py: 4,
    bgcolor: '#F4F7FE'
  },
  errorPaper: {
    p: 4,
    borderRadius: 20,
    bgcolor: 'white',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    border: 'none'
  },
  errorButton: {
    bgcolor: colors.primary.main,
    '&:hover': { bgcolor: colors.primary.dark },
    borderRadius: 12,
    transition: 'all 0.3s ease'
  },

  // Section styles
  section: {
    mb: 5
  },
  sectionTitle: {
    fontSize: '22px',
    fontWeight: '600',
    mb: 2,
    color: colors.text.primary,
    textAlign: 'center'
  },

  // Search section
  searchContainer: {
    position: 'relative',
    maxWidth: 600,
    mx: 'auto',
    mt: 2,
    mb: 4
  },
  searchField: {
    borderRadius: '16px',
    backgroundColor: '#fff',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px',
      paddingRight: '40px',
      '&:hover': {
        boxShadow: '0 6px 20px rgba(108, 99, 255, 0.1)',
      },
      '&.Mui-focused': {
        boxShadow: '0 8px 25px rgba(108, 99, 255, 0.15)',
        borderColor: '#6C63FF',
      }
    }
  },
  searchIcon: {
    mr: 1,
    color: '#6C63FF'
  },
  suggestionsPaper: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    mt: 1,
    maxHeight: 300,
    overflow: 'auto',
    borderRadius: 16,
    bgcolor: 'white',
    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
    border: 'none'
  },
  suggestionItem: {
    '&:hover': {
      bgcolor: '#F0EFFD',
    },
    transition: 'all 0.2s ease'
  },
  suggestionText: {
    color: '#1F2C45'
  },

  // Emotional states section
  emotionalStatesGrid: {
    spacing: 2,
    mb: 5
  },
  emotionalStateButton: {
    borderRadius: '16px',
    flexDirection: 'column',
    px: 2,
    py: 3,
    background: 'linear-gradient(135deg, #7F7FD5 0%, #86A8E7 50%, #91EAE4 100%)',
    color: '#fff',
    minHeight: 100,
    boxShadow: '0 8px 25px rgba(127, 127, 213, 0.3)',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'linear-gradient(135deg, #6C63FF, #00C6FF)',
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 35px rgba(108, 99, 255, 0.4)',
    },
  },
  emotionalStateIcon: {
    fontSize: '32px',
    mb: 1,
    color: 'inherit'
  },
  emotionalStateText: {
    fontWeight: 500,
    fontSize: '12px'
  },

  // Chat section
  chatCard: {
    borderRadius: 20,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.3)',
    border: 'none',
    overflow: 'hidden',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
      pointerEvents: 'none'
    }
  },
  chatCardContent: {
    p: 4,
    position: 'relative',
    zIndex: 1
  },
  chatTitle: {
    mb: 3,
    fontWeight: 600,
    fontSize: '20px'
  },
  chatButtonContainer: {
    display: 'flex',
    gap: 3,
    alignItems: 'center',
    flexWrap: 'wrap'
  },
  chatButton: {
    backgroundColor: '#fff',
    color: '#6C63FF',
    borderRadius: 12,
    px: 4,
    py: 1.5,
    fontWeight: 600,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#F0EFFD',
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 32px rgba(0,0,0,0.2)',
    }
  },
  chatTimeInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  chatTimeIcon: {
    opacity: 0.8
  },
  chatTimeText: {
    opacity: 0.9,
    fontWeight: 500
  },

  // Horizontal scroll containers
  horizontalScrollContainer: {
    display: 'flex',
    overflowX: 'auto',
    pb: 2,
    gap: 2,
    px: 1,
    '&::-webkit-scrollbar': { height: 8 },
    '&::-webkit-scrollbar-thumb': { 
      bgcolor: '#E0DFFF',
      borderRadius: 4
    },
    '&::-webkit-scrollbar-track': {
      bgcolor: '#F2F0FF',
      borderRadius: 4
    }
  },

  // Card styles
  card: {
    cursor: 'pointer',
    borderRadius: 20,
    minWidth: 180,
    mr: 2,
    flexShrink: 0,
    bgcolor: 'white',
    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
    border: 'none',
    transition: 'all 0.3s ease',
    '&:hover': { 
      transform: 'scale(1.03) translateY(-4px)',
      boxShadow: '0 15px 40px rgba(108, 99, 255, 0.2)',
    }
  },
  cardContent: {
    p: 3
  },
  cardIcon: {
    mb: 2,
    color: '#6C63FF',
    fontSize: '32px'
  },
  cardTitle: {
    mb: 1,
    fontWeight: 600,
    color: '#1F2C45',
    fontSize: '16px'
  },
  cardDescription: {
    color: '#697386',
    lineHeight: 1.6,
    fontSize: '14px'
  },

  // Topic cards
  topicCard: {
    backgroundColor: '#FFF3FC',
    minWidth: 240
  },

  // Audio practice cards
  audioCard: {
    backgroundColor: '#E9F5FF',
    minWidth: 240
  },
  audioDurationContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    mt: 1
  },
  audioDurationIcon: {
    color: '#6C63FF',
    fontSize: '16px'
  },
  audioDurationText: {
    color: '#697386',
    fontWeight: 500,
    fontSize: '12px'
  },

  // Course cards
  courseCard: {
    backgroundColor: '#F0EFFD',
    minWidth: 280
  },
  courseMetaContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
    mt: 2
  },
  courseType: {
    color: '#6C63FF',
    fontWeight: 600,
    bgcolor: '#F0EFFD',
    px: 2,
    py: 0.5,
    borderRadius: 8,
    fontSize: '12px'
  },
  courseDurationContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 1
  },
  courseDurationIcon: {
    color: '#6C63FF',
    fontSize: '16px'
  },
  courseDurationText: {
    color: '#697386',
    fontWeight: 500,
    fontSize: '12px'
  },
  courseLessonsText: {
    color: '#697386',
    fontWeight: 500,
    fontSize: '12px'
  },

  // Button styles
  buttonContainer: {
    textAlign: 'center',
    mt: 3,
    display: 'flex',
    justifyContent: 'center'
  },
  outlinedButton: {
    borderColor: '#6C63FF',
    color: '#6C63FF',
    borderRadius: 12,
    px: 4,
    py: 1.5,
    fontWeight: 600,
    transition: 'all 0.3s ease',
    '&:hover': {
      bgcolor: '#6C63FF',
      color: 'white',
      borderColor: '#6C63FF',
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(108, 99, 255, 0.3)',
    }
  }
}; 